import axios from 'axios'
import clsx from 'clsx'
import { addCSRF } from 'lib/utilities'
import { getPathsFromConditions } from 'lib/utilities/form/utilities'
import { mapValues } from 'lodash-es'
import React, { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { TAB_NAVIGATOR } from '../app/Main'
import { setFormName, setHasChanges } from '../features/pages/pageSlice'
import SavingFormBuilderModal from '../modal/SavingModal'
import useLoader from './hooks/useLoader'
import { updateComponentKeys } from '../features/components/componentSlice'
import { splitComponentKey, uniqueComponentKey } from './utility'

const asPercentage = (v) => (String(v).slice(-1) == '%' ? String(v) : `${v}%`)

const getLinkedFields = (template) => {
  let linkedFields = []
  if (template.if) linkedFields = linkedFields.concat(getPathsFromConditions(template.if))
  if (template.interactable) linkedFields = linkedFields.concat(getPathsFromConditions(template.interactable))
  if (template.checkbox && template.checkbox.conditional_logic)
    linkedFields = linkedFields.concat(getPathsFromConditions(template.checkbox.conditional_logic))
  if (linkedFields.length === 0) return []
  return [...new Set(linkedFields)]
}

const useTransformData = () => {
  const templateData = useSelector(({ data }) => data.data)
  const templateCheckbox = useSelector(({ data }) => data.checkbox)
  const components = useSelector(({ components }) => components.all)

  return mapValues({ ...templateData, ...templateCheckbox }, (template, id) => {
    const component = components[template.position]
    return {
      ...template,
      linked_fields: getLinkedFields(template),
      fields: template.paths?.map((path) => path.path),
      id,
      label: component?.behaviorInputs.label,
      placeholder: component?.behaviorInputs.placeholder,
      position: {
        ...mapValues(component.rect, asPercentage),
        page: parseInt(component.page),
      },
    }
  })
}

const useTransformSignatures = () => {
  const signatureData = useSelector(({ data }) => data.signature)
  const components = useSelector(({ components }) => components.all)

  return mapValues(signatureData, (template, id) => {
    return {
      ...template,
      id,
      positions: mapValues(template.positions, (componentKey) => {
        return {
          ...mapValues(components[componentKey]?.rect, asPercentage),
          page: parseInt(components[componentKey]?.page),
        }
      }),
    }
  })
}

const saveForm = async (params) => {
  return await axios.post('form_builder/save_form', addCSRF(params), {
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
  })
}

const NavigatorItem = ({ isSelected, iconClassName, title, onSelect }) => {
  return (
    <li
      onClick={onSelect}
      style={{ padding: '2px 6px' }}
      className="dome-d-flex dome-align-center dome-gap3 dome-cursor-pointer"
    >
      <i
        className={clsx(
          'fa-solid  fa-sm',
          iconClassName,
          isSelected ? 'dome-color-darkest-grey' : 'dome-color-lite-grey'
        )}
      ></i>
      <span className={clsx('dome-p3 ', isSelected ? 'dome-color-darkest-grey' : 'dome-color-lite-grey')}>{title}</span>
    </li>
  )
}

const Navigator = ({ activeTab, onSelect }) => {
  return (
    <ul style={{ gap: 10, listStyle: 'none', padding: 0, margin: 0 }} className="dome-d-flex dome-align-center">
      <NavigatorItem
        title="build"
        iconClassName="fa-screwdriver-wrench"
        isSelected={activeTab === TAB_NAVIGATOR.BUILD}
        onSelect={() => onSelect(TAB_NAVIGATOR.BUILD)}
      />
      <li>
        <i className="fa-regular fa-chevron-right dome-color-lite-grey fa-xs"></i>
      </li>
      <NavigatorItem
        title="assign"
        iconClassName="fa-list-tree"
        isSelected={activeTab === TAB_NAVIGATOR.ASSIGN}
        onSelect={() => onSelect(TAB_NAVIGATOR.ASSIGN)}
      />
      <li>
        <i className="fa-regular fa-chevron-right dome-color-lite-grey fa-xs"></i>
      </li>
      <NavigatorItem
        title="preview"
        iconClassName="fa-magnifying-glass"
        isSelected={activeTab === TAB_NAVIGATOR.PREVIEW}
        // onSelect={() => onSelect(TAB_NAVIGATOR.PREVIEW)}
      />
    </ul>
  )
}

const Header = ({ activeTab, onTabChange, onSave, loading: loadingData }) => {
  const formName = useSelector(({ pages }) => pages.formName)
  const { html_nodes } = useSelector((state) => state.fields)
  const transformedData = useTransformData()
  const transformedSignatures = useTransformSignatures()
  const { withLoading, loading } = useLoader()
  const dispatch = useDispatch()
  const [openTextField, setOpenTextField] = useState(false)

  const isLoading = loading || loadingData
  const selectedKeys = useSelector(({components}) => components.selected)

  const handleSave = useCallback(async () => {
    try {
      const response = await withLoading(() =>
        saveForm({ field_positions: transformedData, signatures: transformedSignatures, form_name: formName, html_nodes: html_nodes })
      )
      const { generated_signatures, generated_positions } = response.data

      const mergeSelectedKeys = selectedKeys.map(key=> {
        const [ type, id, subtype ] = splitComponentKey(key)
        switch(type) {
          case 'data':
          case 'checkbox':
            if (generated_positions[id]) {
              const newKey = uniqueComponentKey(type, generated_positions[id], subtype)
              return splitComponentKey(newKey)
            }
            return splitComponentKey(key)
          case 'signature':
            if (generated_signatures[id]) {
              const newKey = uniqueComponentKey(type, generated_signatures[id], subtype)
              return splitComponentKey(newKey)
            }
            return splitComponentKey(key)
        }
      })
      await onSave({ selectedKeys: mergeSelectedKeys })
      dispatch(setHasChanges(false))
    } catch (error) {
      console.log(error)
    }
  }, [transformedSignatures, transformedData, formName, html_nodes, withLoading, dispatch, onSave])

  const handleEditClick = () => {
    setOpenTextField(true)
  }

  const saveFormName = useCallback(
    (e) => {
      if (!e.target.value) {
        setOpenTextField(false)
      } else {
        dispatch(setFormName(e.target.value))
        setOpenTextField(false)
      }
    },
    [dispatch]
  )

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        saveFormName(e)
      }
    },
    [saveFormName]
  )

  return (
    <div className="frame-header">
      <div>
        {openTextField ? (
          <div className="inputs input-rounded">
            <input defaultValue={formName} onKeyDown={handleKeyDown} onBlur={saveFormName} />
          </div>
        ) : (
          <>
            <div className="form-name">{formName}</div>
            <div className="edit-btn">
              <button onClick={handleEditClick} className="btn-link">
                <i className="fa-light fa-pencil"></i>
                edit
              </button>
            </div>
          </>
        )}
      </div>
      <Navigator activeTab={activeTab} onSelect={(e) => onTabChange(e)} />
      <div className="save-btns">
        <button type="button" className="btn-base btn-green-stroke" onClick={handleSave} disabled={isLoading}>
          {isLoading ? <i className="fa-duotone fa-spinner fa-spin-pulse" /> : 'save'}
        </button>
        <SavingFormBuilderModal onSubmit={handleSave} />
      </div>
    </div>
  )
}

export default Header
